import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { MetaData } from '@/modules/AssetsStudioPage/SensorFusion/types';
import { generateCustomVerticalLines } from './customVerticalLines';
import { generateMockWaveformApiResponse } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Waveform/utils';

import {
  DataLayer,
  AssetCategory,
  API_CONFIG,
  SearchParams,
  GraphDefinition,
  LegendItem,
  ThresholdConfig,
} from './dataLayersApi.types';
import { baseQuery, getSearchParams } from './dataLayersApi.utils';

// Re-export types for backward compatibility
export { DataLayer, AssetCategory };
export type { SearchParams, GraphDefinition, LegendItem, ThresholdConfig };

// =======================
// API Definition
// =======================

export const sensorsDataApi = createApi({
  reducerPath: 'sensorsDataApi',
  baseQuery,
  tagTypes: ['sensorDataMetaData', 'sensorData'],

  // FIX 1: CRITICAL - Increase cache time and disable aggressive refetching
  keepUnusedDataFor: 7200, // 2 hours instead of 1 hour
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false, // CRITICAL: Disable refetch on mount/arg change

  endpoints: (builder) => ({
    fetchSensorDataMetaData: builder.query<MetaData, SearchParams>({
      queryFn: async (params: SearchParams, _queryApi, _extraOptions, baseQuery) => {
        switch (params.data_layer) {
          case DataLayer.OperatorProficiency: {
            const queryParams = new URLSearchParams();
            queryParams.append('months_range', (params.months_range || 6).toString());

            if (params.asset_type && params.asset_type !== 'all') {
              if (Array.isArray(params.asset_type)) {
                params.asset_type.forEach((type) => queryParams.append('asset_type', type));
              } else {
                queryParams.append('asset_type', params.asset_type);
              }
            }

            const result = await baseQuery({ url: `operator_proficiency/metadata?${queryParams.toString()}` });

            if (result.error) {
              return { error: result.error };
            }

            return { data: result.data as MetaData };
          }

          default: {
            const searchParams = getSearchParams(params);
            const result = await baseQuery({ url: `sensors_data/metadata?${searchParams.toString()}` });

            if (result.error) {
              return { error: result.error };
            }

            const response = result.data as MetaData;
            const updatedResponse = { ...response };

            // Filter alarm dataLayer for fixed assets
            if (params.asset_category === AssetCategory.Fixed && updatedResponse.tabs?.sensorFusion?.dataLayers) {
              updatedResponse.tabs.sensorFusion.dataLayers = updatedResponse.tabs.sensorFusion.dataLayers.filter(
                (layer) => !/alarm/i.test(layer.id)
              );
            }

            // MOCK: Inject Waveform tab configuration until BE implements it
            // This allows frontend development to proceed independently
            if (!updatedResponse.tabs) {
              updatedResponse.tabs = {} as any;
            }

            // Get spectrum analysis legend for sensor information
            const spectrumAnalysisLegend = updatedResponse.tabs?.spectrum?.dataLayers?.find(
              (layer: any) => layer.id === 'spectrum_analysis'
            )?.graphs?.[0]?.legend || [];

            updatedResponse.tabs.waveform = {
              enabled: true,
              order: 3, // After spectrum (order 2)
              title: 'Waveform',
              dataLayers: [
                // SENSORS data layer (Vibration Trends) - reuse from spectrum
                {
                  id: 'sensors',
                  enabled: true,
                  title: 'Vibration Trends',
                  order: 1,
                  graphs: updatedResponse.tabs?.sensorFusion?.dataLayers?.find(
                    (layer: any) => layer.id === 'sensors'
                  )?.graphs || [],
                },
                // WAVEFORM data layer - time waveform visualization
                {
                  id: 'waveform',
                  enabled: true,
                  title: 'Time Waveform',
                  order: 2,
                  // Waveform-specific configuration (read by frontend)
                  waveformConfig: {
                    // Duration settings
                    previewDuration: 5,      // seconds - initial preview
                    fullDuration: 30,        // seconds - full waveform on demand
                    // Sample rate (Hz)
                    sampleRate: 4000,
                    // Audio settings
                    audioEnabled: true,
                    audioSampleRate: 44100,  // Hz - for audio playback
                    // Recording types available
                    recordingTypes: [
                      { id: 'acceleration', label: 'Acceleration', unit: 'g', enabled: true, isDefault: true },
                      { id: 'velocity', label: 'Velocity', unit: 'mm/s', enabled: true, isDefault: false },
                    ],
                    // UI feature flags
                    features: {
                      enableMarkers: true,
                      enableHarmonics: false,
                      enableSideBands: false,
                      enableDrawing: false,
                      enableCompareTable: false,
                      enableRegions: true,      // Handlebar selection
                      enableZoom: true,
                      enableLoop: true,
                      showFloatingToolbar: true,
                      showYAxisScale: true,
                      enableDynamicMargin: true,
                    },
                    // Zoom settings
                    zoom: {
                      min: 50,      // pixels per second
                      max: 1000,
                      step: 1.5,
                      default: 100,
                    },
                    // Autoscale settings
                    autoscale: {
                      mode: 'onDataLoad',  // 'none' | 'onload' | 'onDataLoad'
                      axis: 'both',        // 'x' | 'y' | 'both'
                    },
                  },
                  graphs: [
                    {
                      id: 'time_waveform',
                      title: 'Time Waveform',
                      enabled: true,
                      chartType: 'waveform',  // Custom chart type for waveform
                      splitToggle: false,
                      multiSelect: false,  // Single select for timestamps (features) - expand to multiSelect later
                      xAxis: { label: 'Time', unit: 's' },
                      yAxis: [
                        { id: 'acceleration', label: 'Acceleration', unit: 'g', position: 'left' },
                        { id: 'velocity', label: 'Velocity', unit: 'mm/s', position: 'right' },
                      ],
                      legend: spectrumAnalysisLegend.map((item: any) => ({
                        ...item,
                        enabled: true,
                      })),
                      // Features = Timestamps (populated dynamically from sensorFusionVerticalLines)
                      // Each timestamp becomes a feature badge in toolbar (single select for now)
                      features: [], // Populated dynamically from timestamps
                    },
                  ],
                  // Expandable Legend = Acceleration/Velocity (the series within each timestamp's graph)
                  expandableLegend: {
                    enabled: true,
                    label: 'Series',
                    items: [
                      { id: 'acceleration', label: 'Acceleration', color: null, enabled: true },
                      { id: 'velocity', label: 'Velocity', color: null, enabled: true },
                    ],
                    defaultSelection: ['acceleration', 'velocity'],
                    multiSelect: true,
                  },
                },
              ],
            } as any;

            const finalResponse = {
              ...updatedResponse,
              operational_mode: {
                enabled: params.asset_category === AssetCategory.Mobile,
                options: [{ id: 'full_pull', label: 'Full Pull', enabled: false }],
              },
            };

            return { data: finalResponse };
          }
        }
      },
      // FIX 2: Add proper cache key generation to prevent unnecessary requests
      serializeQueryArgs: ({ queryArgs }: { queryArgs: SearchParams }) => {
        // Create stable cache key based on essential params only
        const {
          site_id,
          asset_id,
          component_id,
          asset_category,
          asset_name,
          data_layer,
          full_pull = false,
          from_date,
          to_date,
        } = queryArgs;

        return JSON.stringify({
          site_id,
          asset_id,
          component_id,
          asset_category,
          asset_name,
          data_layer,
          full_pull,
          from_date,
          to_date,
        });
      },
      // Keep cached data for shorter time to ensure fresh metadata
      keepUnusedDataFor: 300, // 5 minutes instead of default
      // Provide cache tags for invalidation
      providesTags: (_, __, arg) => [{ type: 'sensorDataMetaData', id: `${arg.component_id}` }, 'sensorDataMetaData'],
    }),
    fetchSensorVisualAIMetaData: builder.query<MetaData, SearchParams>({
      queryFn: async (params: SearchParams, _queryApi, _extraOptions, baseQuery) => {
        // Return mock data directly for operator proficiency
        console.log('params.data_layer', params.data_layer);

        // For other data layers, use the regular baseQuery
        const searchParams = getSearchParams(params);
        const result = await baseQuery({ url: `sensors_data/metadata?page=visual_ai&${searchParams.toString()}` });

        if (result.error) {
          return { error: result.error };
        }

        // Apply transformations for regular sensor data
        const response = result.data as MetaData;
        const updatedResponse = { ...response };
        //TODO: add to each datalayer:   showSectionControls={false} isExpandable={false} //FIXME: add filed from api
        // updatedResponse.tabs.beltMisalignment.dataLayers.push({
        //   enabled: true,
        //   filters: [],
        //   dataLayerType:'list',
        //   // graphs: [],
        //   list: [
        //     // {
        //     //   title: fault_type
        //     //     ? fault_type
        //     //     : health_status === HEALTH_STATUS.HEALTHY
        //     //     ? 'No Fault Detected'
        //     //     : 'Fault Detected',
        //     //   health_status,
        //     //   timestamp,
        //     // },
        //   ],
        //   id: 'componentEvidence',
        //   title: 'Component Evidence',
        // });

        console.log('updatedResponse', updatedResponse);

        // Append the operational_mode config
        const finalResponse = {
          ...updatedResponse,
          operational_mode: {
            enabled: params.asset_category === 'mobile',
            options: [{ id: 'full_pull', label: 'Full Pull', enabled: false }],
          },
        };
        console.log('finalResponse', finalResponse);

        return { data: finalResponse };
      },
      // FIX 2: Add proper cache key generation to prevent unnecessary requests
      serializeQueryArgs: ({ queryArgs }: { queryArgs: SearchParams }) => {
        // Create stable cache key based on essential params only
        const {
          site_id,
          asset_id,
          component_id,
          asset_category,
          asset_name,
          data_layer,
          full_pull = false,
          from_date,
          to_date,
        } = queryArgs;

        return JSON.stringify({
          site_id,
          asset_id,
          component_id,
          asset_category,
          asset_name,
          data_layer,
          full_pull,
          from_date,
          to_date,
        });
      },
      // Keep cached data for shorter time to ensure fresh metadata
      keepUnusedDataFor: 300, // 5 minutes instead of default
      // Provide cache tags for invalidation
      providesTags: (result, error, arg) => [
        { type: 'sensorDataMetaData', id: `${arg.component_id}` },
        'sensorDataMetaData',
      ],
    }),

    // Mutation endpoint (for backward compatibility)
    fetchSensorData: builder.mutation<any, { params: SearchParams; body: GraphDefinition; signal?: AbortSignal }>({
      queryFn: async ({ params, body, signal }, _queryApi, _extraOptions, baseQuery) => {
        switch (params.data_layer) {
          // MOCK: Return mock waveform data for development
          // DISABLED: Waveform mock data causes performance issues with large datasets
          // case 'waveform': {
          //   // Get sensor names from legend (preserves original legend)
          //   const sensorNames = body.legend
          //     ?.filter((item: any) => item.enabled)
          //     .map((item: any) => item.displayName || item.name) || ['Sensor 1', 'Sensor 2'];

          //   // Default to 5 seconds for performance (20,000 samples vs 120,000 for 30 sec)
          //   const waveformDuration = (body as any).waveformDuration || 5;
          //   const sampleRate = 4000;
          //   const mockResponse = generateMockWaveformApiResponse(sensorNames, waveformDuration, sampleRate);

          //   return { data: mockResponse };
          // }

          case DataLayer.OperatorProficiency: {
            // Build query params - use body.id directly as data_layer (BE now uses direct IDs)
            const queryParams = new URLSearchParams();
            queryParams.append('data_layer', body.id);

            // Add months_range to URL
            if (params.months_range) {
              queryParams.append('months_range', String(params.months_range));
            }

            // Add assets_type from filter values (required)
            const assetTypes = params.asset_type as string[];
            assetTypes?.forEach((type) => queryParams.append('assets_type', type));

            // Extract graph definition from graphs[0] and flatten into body
            const graphDef = (body as any).graphs?.[0] || {};
            const dataLayerType = (body as any).type || graphDef.type || 'table';

            // Build body with dataLayer fields + flattened graph fields (with defaults)
            const opBody: Record<string, unknown> = {
              // Filter params
              months_range: params.months_range || 6,
              asset_type: assetTypes,
              // DataLayer fields
              id: body.id,
              type: dataLayerType,
              title: (body as any).title,
              enabled: (body as any).enabled ?? true,
              // Graph-specific fields (flattened from graphs[0]) with defaults
              chartType: graphDef.chartType || graphDef.type || 'table',
              height: graphDef.height ?? 200,
              showTotal: graphDef.showTotal ?? false,
              items: graphDef.items ?? 'dynamic',
              showPercentage: graphDef.showPercentage ?? false,
              showIcon: graphDef.showIcon ?? false,
              maxItems: graphDef.maxItems ?? 10,
              columns: graphDef.columns,
              legend: graphDef.legend || [],
            };

            const result = await baseQuery({
              url: `operator_proficiency/data?${queryParams.toString()}`,
              method: 'POST',
              body: opBody,
              signal,
            });

            if (result.error) {
              return { error: result.error };
            }

            return { data: result.data };
          }
          case DataLayer.BeltMisalignment:
          case DataLayer.RockSize: {
            const searchParams = getSearchParams(params);

            const sensorDataType = params.data_layer;

            const result = await baseQuery({
              url: `sensors_data/${sensorDataType}?${searchParams.toString()}`,
              method: 'POST',
              body,
              signal,
            });
            if (result.error) {
              return { error: result.error };
            }

            const enhancedData = result.data as any;

            enhancedData.shapes = {
              levels: body?.levels,
              isMirrored: body.id === 'leftRoller',
              shouldCalculate: params.data_layer === DataLayer.BeltMisalignment,
            };

            // const chartId = `${body.id}-combined`;
            // enhancedData.customVerticalLines = generateCustomVerticalLines(chartId, enhancedData);

            return { data: enhancedData };
          }

          default: {
            const searchParams = getSearchParams(params);
            const isSpectral = searchParams.get('is_spectral') === 'true';
            const sensorDataType = isSpectral ? 'spectral_data' : 'data';
            searchParams.delete('is_spectral');

            const result = await baseQuery({
              url: `sensors_data/${sensorDataType}?${searchParams.toString()}`,
              method: 'POST',
              body,
              signal,
            });

            if (result.error) {
              return { error: result.error };
            }

            const enhancedData = result.data as any;

            if (API_CONFIG.enableMockCustomLines) {
              const chartId = `${body.id}-combined`;
              enhancedData.customVerticalLines = generateCustomVerticalLines(chartId, enhancedData);
            }

            return { data: enhancedData };
          }
        }
      },
      // FIX 4: For mutations, we can't use serializeQueryArgs, but we can add request tracking
      // Instead, we'll rely on proper cancellation in the hook
      extraOptions: {
        timeout: 30000, // 30 second timeout
      },
    }),

    // Query endpoint with caching
    fetchSensorDataQuery: builder.query<any, { params: SearchParams; body: GraphDefinition }>({
      queryFn: async ({ params, body }, _queryApi, _extraOptions, baseQuery) => {
        switch (params.data_layer) {
          // MOCK: Return mock waveform data for development
          // DISABLED: Waveform mock data causes performance issues with large datasets
          // case 'waveform': {
          //   const sensorNames = body.legend
          //     ?.filter((item: any) => item.enabled)
          //     .map((item: any) => item.displayName || item.name) || ['Sensor 1', 'Sensor 2'];

          //   const waveformDuration = (body as any).waveformDuration || 5;
          //   const sampleRate = 4000;
          //   const mockResponse = generateMockWaveformApiResponse(sensorNames, waveformDuration, sampleRate);
          //   return { data: mockResponse };
          // }

          case DataLayer.OperatorProficiency: {
            // Build query params - use body.id directly as data_layer (BE now uses direct IDs)
            const queryParams = new URLSearchParams();
            queryParams.append('data_layer', body.id);

            // Add months_range to URL
            if (params.months_range) {
              queryParams.append('months_range', String(params.months_range));
            }

            // Add assets_type from filter values (required)
            const assetTypes = params.asset_type as string[];
            assetTypes?.forEach((type) => queryParams.append('assets_type', type));

            // Extract graph definition from graphs[0] and flatten into body
            const graphDef = (body as any).graphs?.[0] || {};
            const dataLayerType = (body as any).type || graphDef.type || 'table';

            // Build body with dataLayer fields + flattened graph fields (with defaults)
            const opBody: Record<string, unknown> = {
              // Filter params
              months_range: params.months_range || 6,
              asset_type: assetTypes,
              // DataLayer fields
              id: body.id,
              type: dataLayerType,
              title: (body as any).title,
              enabled: (body as any).enabled ?? true,
              // Graph-specific fields (flattened from graphs[0]) with defaults
              chartType: graphDef.chartType || graphDef.type || 'table',
              height: graphDef.height ?? 200,
              showTotal: graphDef.showTotal ?? false,
              items: graphDef.items ?? 'dynamic',
              showPercentage: graphDef.showPercentage ?? false,
              showIcon: graphDef.showIcon ?? false,
              maxItems: graphDef.maxItems ?? 10,
              columns: graphDef.columns,
              legend: graphDef.legend || [],
            };

            const result = await baseQuery({
              url: `operator_proficiency/data?${queryParams.toString()}`,
              method: 'POST',
              body: opBody,
            });

            if (result.error) {
              return { error: result.error };
            }

            return { data: result.data };
          }

          default: {
            const searchParams = getSearchParams(params);
            const result = await baseQuery({
              url: `sensors_data/data?${searchParams.toString()}`,
              method: 'POST',
              body,
            });

            if (result.error) {
              return { error: result.error };
            }

            const enhancedData = result.data as any;

            if (API_CONFIG.enableMockCustomLines) {
              const chartId = `${body.id}-combined`;
              enhancedData.customVerticalLines = generateCustomVerticalLines(chartId, enhancedData);
            }

            return { data: enhancedData };
          }
        }
      },
      // Create a stable cache key that includes both params and body
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs || !queryArgs.params || !queryArgs.body) {
          return 'invalid-query';
        }

        const { params, body } = queryArgs;
        const {
          site_id,
          asset_id,
          component_id,
          asset_category,
          from_date,
          to_date,
          data_layer,
          full_pull = false,
        } = params;

        // Include relevant body fields in cache key
        const bodyKey = {
          id: body.id,
          enabled: body.enabled,
          legendCount: body.legend?.length || 0,
          enabledLegends: body.legend
            ?.filter((l: any) => l.enabled)
            .map((l: any) => l.displayName)
            .sort(),
          features: body.features?.map((f: any) => f.id).sort(),
        };

        return JSON.stringify({
          site_id,
          asset_id,
          component_id,
          asset_category,
          from_date,
          to_date,
          data_layer,
          full_pull,
          bodyKey,
        });
      },
      // Cache for 30 minutes
      keepUnusedDataFor: 1800,
      // Provide cache tags for targeted invalidation
      providesTags: (_, __, { params, body }) => [
        {
          type: 'sensorData',
          id: `${params?.component_id}-${body?.id}`,
        },
        'sensorData',
      ],
    }),
  }),
});

// =======================
// Export Hooks
// =======================

export const {
  useFetchSensorDataMetaDataQuery,
  useFetchSensorVisualAIMetaDataQuery,
  useLazyFetchSensorDataMetaDataQuery,
  useFetchSensorDataMutation,
  useFetchSensorDataQueryQuery,
  useLazyFetchSensorDataQueryQuery,
} = sensorsDataApi;

export type SensorApiResponse = any;
export type GraphReading = any;
